import { useNavigate, useLocation, useSearchParams } from "react-router";
import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import LogoutModal from "./LogoutModal";
import { Spinner } from "react-bootstrap";
import { logout } from "../services/firebaseAuthService";
import { db } from "../services/firebase";
import { useAuth } from "../hooks/useAuth";
import { useGemeinde } from "../context/GemeindeContext";
import Gemeinde, { gemeindeConverter } from "../DTOs/Gemeinde";
import { Globals } from "../globals";
import { markLanguageChosen } from "../i18n/gemeindeLanguage";
import { useTranslation } from "react-i18next";
import { useColorScheme } from "../hooks/useColorScheme";
import { nextColorSchemePreference } from "../theme/colorScheme";

// Admin tabs are driven through the `?tab=` query param on the /admin route so
// that this shared sidebar can link straight to a tab from anywhere in the app.
const ADMIN_VERWALTUNG_TABS = ["preachers", "choirsongs", "sermonplan"] as const;
const ADMIN_SYSTEM_TABS = ["notifications", "members"] as const;
const ADMIN_TAB_LABELS: Record<string, string> = {
    preachers: "admin.preacherManagement",
    choirsongs: "admin.choirSongManagement",
    sermonplan: "admin.sermonPlanImport",
    notifications: "admin.notificationSettings",
    members: "members.title",
};
const DEFAULT_ADMIN_TAB = "preachers";

export default function AppSidebar() {
    const { t, i18n } = useTranslation();
    const { preference: colorScheme, cyclePreference: cycleColorScheme } = useColorScheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [showModal, setShowModal] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { isAuthenticated, loading: authLoading, user, isSuperAdmin, canEdit, gemeindeId } = useAuth();
    const { activeGemeindeId, activeGemeinde, setActiveGemeindeId } = useGemeinde();
    const [gemeinden, setGemeinden] = useState<Gemeinde[]>([]);

    // Superadmin: load the Gemeinde list for the switcher.
    useEffect(() => {
        if (!isSuperAdmin) return;
        getDocs(collection(db, Globals.GEMEINDEN_COLLECTION).withConverter(gemeindeConverter))
            .then((snap) => setGemeinden(snap.docs.map((d) => d.data())))
            .catch(() => setGemeinden([]));
    }, [isSuperAdmin]);

    const getCurrentLanguage = () => (i18n.resolvedLanguage || i18n.language || "de").split("-")[0];
    const toggleLanguage = () => {
        // An explicit choice wins over the Gemeinde's default language (GOD-50).
        markLanguageChosen();
        i18n.changeLanguage(getCurrentLanguage() === "de" ? "en" : "de");
    };

    // Navigate and always close the mobile drawer afterwards.
    const go = (path: string) => {
        setMobileOpen(false);
        navigate(path);
    };

    function handleLogout() {
        setShowModal(false);
        setMobileOpen(false);
        logout().then(() => {
            navigate(Globals.PATH_DEFAULT, { state: { fromLogout: true } });
        });
    }

    const onList = location.pathname === Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST;
    const onForm = location.pathname === Globals.PATH_SIMPLE_SUNDAY_SERVICE;
    const onAdmin = location.pathname.startsWith(Globals.PATH_ADMIN);
    const onSuperadmin = location.pathname.startsWith(Globals.PATH_SUPERADMIN);
    const onSettings = location.pathname.startsWith(Globals.PATH_GEMEINDE_SETTINGS);
    const activeAdminTab = searchParams.get("tab") || DEFAULT_ADMIN_TAB;
    const currentLang = getCurrentLanguage();

    // Whether Gemeinde-scoped navigation (logbook, admin) should be shown.
    const hasGemeinde = !!activeGemeindeId;
    const brandSub = onSuperadmin
        ? "Superadmin"
        : onAdmin
          ? "Admin"
          : onSettings
            ? (t("nav.gemeinde") as string)
            : (t("nav.logbook") as string);

    const renderAdminTab = (tab: string) => (
        <button
            key={tab}
            className={`gl-side-btn${onAdmin && activeAdminTab === tab ? " active" : ""}`}
            onClick={() => go(`${Globals.PATH_ADMIN}?tab=${tab}`)}
        >
            {t(ADMIN_TAB_LABELS[tab])}
        </button>
    );

    return (
        <>
            {/* Mobile-only top bar with a hamburger that opens the drawer */}
            <div className="gl-topbar">
                <button
                    className="gl-topbar-toggle"
                    onClick={() => setMobileOpen(true)}
                    aria-label={t("nav.openNavigation") as string}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                </button>
                <a
                    className="gl-topbar-brand"
                    href={Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST}
                    onClick={(e) => { e.preventDefault(); go(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST); }}
                >
                    {/* Word mark instead of the JW logo; the jw29435 mark sits in the footer (GOD-57). */}
                    <span className="gl-wordmark">Serva</span>
                </a>
            </div>

            <aside className={`gl-sidebar${mobileOpen ? " open" : ""}`}>
                <a
                    className="gl-sidebar-brand"
                    href={Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST}
                    onClick={(e) => { e.preventDefault(); go(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST); }}
                >
                    <span className="gl-wordmark">Serva</span>
                    <span className="gl-brand-sub">{brandSub}</span>
                </a>

                {!authLoading && isAuthenticated && (
                    <>
                        {/* Superadmin: Gemeinde switcher + management area */}
                        {isSuperAdmin && (
                            <>
                                <div className="gl-section-label">Superadmin</div>
                                <select
                                    className="form-select form-select-sm mb-2"
                                    value={activeGemeindeId ?? ""}
                                    onChange={(e) => setActiveGemeindeId(e.target.value || null)}
                                    aria-label={t("gemeinde.switch") as string}
                                >
                                    <option value="">{t("gemeinde.choose")}</option>
                                    {gemeinden.map((g) => (
                                        <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                </select>
                                <button
                                    className={`gl-side-btn${onSuperadmin ? " active" : ""}`}
                                    onClick={() => go(Globals.PATH_SUPERADMIN)}
                                >
                                    {t("superadmin.title")}
                                </button>
                            </>
                        )}

                        {/* Logbook – everyone assigned to a Gemeinde sees the list. */}
                        {hasGemeinde && (
                            <>
                                <div className="gl-section-label">{t("nav.logbook")}</div>
                                <button
                                    className={`gl-side-btn${onList ? " active" : ""}`}
                                    onClick={() => go(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST)}
                                >
                                    {t("simpleSundayService.listTitle")}
                                </button>
                                {canEdit && (
                                    <button
                                        className={`gl-side-btn${onForm ? " active" : ""}`}
                                        onClick={() => go(Globals.PATH_SIMPLE_SUNDAY_SERVICE)}
                                    >
                                        {t("simpleSundayService.heading")}
                                    </button>
                                )}
                            </>
                        )}

                        {/* Gemeinde administration (Gemeinde admin / superadmin in a Gemeinde). */}
                        {canEdit && hasGemeinde && (
                            <>
                                <div className="gl-section-label">{t("nav.management")}</div>
                                {ADMIN_VERWALTUNG_TABS.map(renderAdminTab)}

                                <div className="gl-section-label">{t("nav.system")}</div>
                                {ADMIN_SYSTEM_TABS.map(renderAdminTab)}
                                <button
                                    className={`gl-side-btn${onSettings ? " active" : ""}`}
                                    onClick={() => go(Globals.PATH_GEMEINDE_SETTINGS)}
                                >
                                    {t("gemeinde.settings")}
                                </button>
                            </>
                        )}
                    </>
                )}

                <div className="mt-auto pt-3">
                    <div className="gl-section-label" style={{ paddingTop: 0 }}>{t("nav.account")}</div>

                    {!authLoading && isAuthenticated && (activeGemeinde || gemeindeId) && (
                        <div className="gl-side-btn" style={{ cursor: "default", overflow: "hidden" }}>
                            <span className="text-truncate gl-meta" style={{ color: "var(--gl-ink-soft)" }}>
                                {activeGemeinde?.name ?? ""}
                            </span>
                        </div>
                    )}

                    <button className="gl-side-btn" onClick={toggleLanguage}>
                        <span>{currentLang === "de" ? "Deutsch" : "English"}</span>
                        <span className="gl-side-count">{currentLang === "de" ? "→ EN" : "→ DE"}</span>
                    </button>

                    {/* Appearance (GOD-61): automatic → light → dark, per device. */}
                    <button className="gl-side-btn" onClick={cycleColorScheme} title={t("appearance.label") as string}>
                        <span>{t(`appearance.${colorScheme}`)}</span>
                        <span className="gl-side-count">→ {t(`appearance.${nextColorSchemePreference(colorScheme)}`)}</span>
                    </button>

                    {authLoading ? (
                        <div className="gl-side-btn" style={{ cursor: "default" }}>
                            <Spinner animation="grow" size="sm" />
                        </div>
                    ) : isAuthenticated ? (
                        <>
                            <div className="gl-side-btn" style={{ cursor: "default", overflow: "hidden" }}>
                                <span className="text-truncate gl-meta" style={{ color: "var(--gl-ink-soft)" }}>
                                    {user?.email}
                                </span>
                            </div>
                            <button className="gl-side-btn" onClick={() => setShowModal(true)}>
                                {t("common.logout")}
                            </button>
                        </>
                    ) : (
                        <div className="gl-side-btn gl-meta" style={{ cursor: "default" }}>
                            {t("auth.notSignedIn")}
                        </div>
                    )}
                </div>
            </aside>

            {mobileOpen && <div className="gl-backdrop" onClick={() => setMobileOpen(false)} />}

            <LogoutModal show={showModal} handleClose={() => setShowModal(false)} handleLogout={handleLogout} />
        </>
    );
}
